import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchDiscoverSalon } from '../../api/discover';
import { fetchSalonBranches } from '../../api/clientBooking';
import BankAccountCards from '../../components/payments/BankAccountCards';
import { createOrder } from '../../api/orders';
import DashboardShell from '../../components/ui/DashboardShell';
import Input from '../../components/ui/Input';
import { useCart } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const { items, total, clearCart } = useCart();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [branchId, setBranchId] = useState('');
  const [placed, setPlaced] = useState(null);

  const tenantId = items[0]?.tenantId;
  const tenantIds = [...new Set(items.map((i) => i.tenantId))];

  const { data: branchData } = useQuery({
    queryKey: ['checkout-branches', tenantId],
    queryFn: () => fetchSalonBranches(tenantId),
    enabled: !!tenantId && tenantIds.length === 1,
  });

  const { data: salonData } = useQuery({
    queryKey: ['checkout-salon', tenantId],
    queryFn: () => fetchDiscoverSalon(tenantId),
    enabled: !!tenantId && tenantIds.length === 1,
  });

  const branches = useMemo(() => branchData?.branches || [], [branchData]);
  const bankAccounts = salonData?.salon?.bankAccounts || [];

  useEffect(() => {
    if (!items.length) return;
    const preset = items.find((i) => i.branchId)?.branchId;
    if (preset) setBranchId(preset);
    else if (branches.length) {
      const main = branches.find((b) => b.isMain) || branches[0];
      setBranchId(main._id);
    }
  }, [items, branches]);

  const orderMut = useMutation({
    mutationFn: createOrder,
    onSuccess: (res) => {
      clearCart();
      setPlaced(res.order);
      showToast(t('toast.orderPlaced'), 'success');
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.orderFailed'), 'error'),
  });

  if (!items.length && !placed) {
    return (
      <DashboardShell title={t('orders.checkout.title')}>
        <p>
          {t('orders.checkout.cartEmpty')}{' '}
          <Link to="/marketplace">{t('orders.checkout.cartEmptyLink')}</Link>
        </p>
      </DashboardShell>
    );
  }

  if (tenantIds.length > 1) {
    return (
      <DashboardShell title={t('orders.checkout.title')}>
        <p className="text-amber-400">{t('orders.checkout.oneSalonAtATime')}</p>
        <Link to="/client/cart" className="mt-4 inline-block text-glow-400">{t('orders.checkout.backToCart')}</Link>
      </DashboardShell>
    );
  }

  if (placed) {
    return (
      <DashboardShell title={t('orders.checkout.orderPlacedTitle')}>
        <div className="mx-auto max-w-md rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center">
          <p className="text-4xl">✓</p>
          <h2 className="mt-4 text-xl font-semibold">{t('orders.checkout.thankYou')}</h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">{t('orders.checkout.orderId', { id: placed._id?.slice(-8).toUpperCase() })}</p>
          <p className="mt-2 text-lg text-glow-300">{t('common.currency', { amount: placed.totalAmount?.toLocaleString() })}</p>
          <button
            type="button"
            onClick={() => navigate('/client/orders')}
            className="mt-6 w-full rounded-lg bg-glow-600 py-2.5 text-sm text-white"
          >
            {t('orders.checkout.trackOrder')}
          </button>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={t('orders.checkout.title')} subtitle={t('orders.checkout.totalSubtitle', { amount: total.toLocaleString() })}>
      <form
        className="max-w-xl space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!branchId) {
            showToast(t('toast.selectBranchCheckout'), 'error');
            return;
          }
          orderMut.mutate({
            branchId,
            items: items.map((i) => ({ productId: i.productId, quantity: i.quantity })),
            shippingAddress: { street, city, postalCode, phone },
            notes,
          });
        }}
      >
        <div>
          <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('orders.checkout.branchRequired')}</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            required
            className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
          >
            <option value="">{t('appointments.selectBranch')}</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>
        <Input label={t('orders.checkout.street')} value={street} onChange={(e) => setStreet(e.target.value)} required />
        <Input label={t('orders.checkout.city')} value={city} onChange={(e) => setCity(e.target.value)} required />
        <Input label={t('orders.checkout.postalCode')} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
        <Input label={t('orders.checkout.contactPhone')} value={phone} onChange={(e) => setPhone(e.target.value)} required />
        <div>
          <label className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.notes')}</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm" />
        </div>
        {bankAccounts.length > 0 && (
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4">
            <BankAccountCards accounts={bankAccounts} />
            <p className="mt-2 text-xs text-zinc-500">{t('orders.checkout.slipHint')}</p>
          </div>
        )}
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 text-sm">
          <p className="font-medium">{t('orders.checkout.orderSummary')}</p>
          {items.map((i) => (
            <p key={i.productId} className="mt-1 text-zinc-600 dark:text-zinc-500">
              {t('orders.checkout.summaryLine', {
                name: i.name,
                quantity: i.quantity,
                amount: (i.price * i.quantity).toLocaleString(),
              })}
            </p>
          ))}
          <p className="mt-3 font-semibold">{t('client.cart.totalLabel', { amount: total.toLocaleString() })}</p>
        </div>
        <button type="submit" disabled={orderMut.isPending} className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white disabled:opacity-50">
          {orderMut.isPending ? t('orders.checkout.placingOrder') : t('orders.checkout.placeOrder')}
        </button>
      </form>
    </DashboardShell>
  );
};

export default CheckoutPage;
